import React, { useMemo, useState } from 'react';
import { linkifyText } from './linkify';
import { PassData, maskIfSensitiveCode } from './types';

// Label/value rows sourced from the real pass data (front slots + back tags).
// Shows the first few rows with a "View full details" reveal when there are
// more — matches the target design's Pass details card.
const VISIBLE_ROWS = 5;

const PassDetailsSection: React.FC<{ passData: PassData }> = ({ passData }) => {
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const populated = (f?: { label?: string; value?: string } | null) =>
      !!f && !!(f.label || f.value);
    const slots = [
      passData.position_header,
      passData.position_one,
      passData.position_two,
      passData.position_three,
      passData.position_four,
      passData.position_aux_one,
      passData.position_aux_two,
      passData.position_aux_three,
      passData.position_aux_four,
    ].filter(populated) as Array<{ label: string; value: string }>;
    return [...slots, ...(passData.tags ?? [])].map((f) => maskIfSensitiveCode(f, passData));
  }, [passData]);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">No extra details configured for this pass.</p>;
  }

  const visible = showAll ? rows : rows.slice(0, VISIBLE_ROWS);

  return (
    <div>
      <dl className="space-y-3">
        {visible.map((row, idx) => (
          <div key={`${row.label}-${idx}`} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
            <dt className="text-sm text-gray-500 sm:w-36 shrink-0">{row.label}</dt>
            <dd className="text-sm font-medium text-gray-900 break-words whitespace-pre-line">
              {linkifyText(row.value, '#0A3B2C')}
            </dd>
          </div>
        ))}
      </dl>
      {rows.length > VISIBLE_ROWS && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-4 w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-[#F5F8F1] text-sm font-medium text-gray-800 hover:bg-[#ECF2E6] transition"
        >
          {showAll ? 'Show fewer details' : 'View full details'}
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${showAll ? 'rotate-90' : ''}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default PassDetailsSection;
